import { Callout, CollapsiblePaper, CopyToClipboardButton, Page } from '@app-builder/components';
import { ExternalLink } from '@app-builder/components/ExternalLink';
import { Nudge } from '@app-builder/components/Nudge';
import { CreateApiKey } from '@app-builder/components/Settings/ApiKey/CreateApiKey';
import { DeleteApiKey } from '@app-builder/components/Settings/ApiKey/DeleteApiKey';
import { CreateWebhook } from '@app-builder/components/Settings/Webhooks/CreateWebhook';
import { EventTypes } from '@app-builder/components/Webhooks/EventTypes';
import { authMiddleware } from '@app-builder/middlewares/auth-middleware';
import { type ApiKey, type CreatedApiKey } from '@app-builder/models/api-keys';
import { type Webhook } from '@app-builder/models/webhook';
import { useAuthSession } from '@app-builder/services/auth/auth-session.server';
import { webhooksSetupDocHref } from '@app-builder/services/documentation-href';
import {
  isAccessible,
  isCreateApiKeyAvailable,
  isCreateWebhookAvailable,
  isDeleteApiKeyAvailable,
  isReadApiKeyAvailable,
  isReadWebhookAvailable,
} from '@app-builder/services/feature-access';
import { tKeyForApiKeyRole } from '@app-builder/services/i18n/translation-keys/api-key';
import { downloadFile } from '@app-builder/utils/download-file';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { createColumnHelper } from '@tanstack/react-table';
import { type FeatureAccessLevelDto } from 'marble-api/generated/feature-access-api';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import type { MarbleTableFeatures } from 'ui-design-system';
import { Button, Table, Typo, useTable } from 'ui-design-system';
import { Icon } from 'ui-icons';

const apiKeysLoader = createServerFn()
  .middleware([authMiddleware])
  .handler(async function apiKeysLoader({ context }) {
    const { apiKey, webhookRepository, dataModelRepository, user, entitlements } = context.authInfo;

    if (!isReadApiKeyAvailable(user)) throw redirect({ to: '/' });

    const [apiKeys, openapiV1] = await Promise.all([
      apiKey.listApiKeys(),
      dataModelRepository.getOpenApiSpecOfVersion('v1'),
    ]);

    const authSession = await useAuthSession();
    const createdApiKey = authSession.data.createdApiKey;
    if (createdApiKey) {
      await authSession.update({ createdApiKey: undefined });
    }

    const canReadWebhooks = isReadWebhookAvailable(user);
    let webhooks: Webhook[] = [];
    let webhooksError = false;
    if (canReadWebhooks) {
      try {
        webhooks = await webhookRepository.listWebhooks();
      } catch {
        webhooksError = true;
      }
    }

    return {
      apiKeys,
      openapiV1,
      createdApiKey,
      isCreateApiKeyAvailable: isCreateApiKeyAvailable(user),
      isDeleteApiKeyAvailable: isDeleteApiKeyAvailable(user),
      webhooks,
      canReadWebhooks,
      webhooksError,
      isCreateWebhookAvailable: canReadWebhooks && !webhooksError && isCreateWebhookAvailable(user),
      webhooksStatus: entitlements.webhooks,
    };
  });

export const Route = createFileRoute('/_app/_builder/settings/api-keys')({
  loader: () => apiKeysLoader(),
  component: ApiKeys,
});

const apiKeyColumnHelper = createColumnHelper<MarbleTableFeatures, ApiKey>();
const webhookColumnHelper = createColumnHelper<MarbleTableFeatures, Webhook>();

function ApiKeys() {
  const { t } = useTranslation(['common', 'settings', 'api']);
  const {
    apiKeys,
    openapiV1,
    createdApiKey,
    isCreateApiKeyAvailable,
    isDeleteApiKeyAvailable,
    webhooks,
    canReadWebhooks,
    webhooksError,
    isCreateWebhookAvailable,
    webhooksStatus,
  } = Route.useLoaderData();

  const apiKeyColumns = useMemo(() => {
    return [
      apiKeyColumnHelper.accessor((row) => row.prefix, {
        id: 'prefix',
        header: t('settings:api_keys.value'),
        size: 100,
        cell: ({ getValue }) => {
          return <span>{`${getValue()}*************`}</span>;
        },
      }),
      apiKeyColumnHelper.accessor((row) => row.description, {
        id: 'description',
        header: t('settings:api_keys.description'),
        size: 300,
      }),
      apiKeyColumnHelper.accessor((row) => row.role, {
        id: 'role',
        header: t('settings:api_keys.role'),
        size: 150,
        cell: ({ getValue }) => t(tKeyForApiKeyRole(getValue())),
      }),
      ...(isDeleteApiKeyAvailable
        ? [
            apiKeyColumnHelper.display({
              id: 'actions',
              size: 100,
              cell: ({ cell }) => {
                return (
                  <div className="group-hover:text-grey-primary focus-within:text-grey-primary text-transparent">
                    <DeleteApiKey apiKey={cell.row.original} />
                  </div>
                );
              },
            }),
          ]
        : []),
    ];
  }, [isDeleteApiKeyAvailable, t]);

  const apiKeyTable = useTable({
    data: apiKeys,
    columns: apiKeyColumns,
    columnResizeMode: 'onChange',
  });

  return (
    <Page.Content width="readable">
      <div className="flex flex-col items-start gap-sm">
        <Button
          variant="secondary"
          onClick={() => {
            try {
              const blob = new Blob([JSON.stringify(openapiV1)], {
                type: 'application/json;charset=utf-8,',
              });
              const url = URL.createObjectURL(blob);
              void downloadFile(url, 'openapi-v1.json');
            } catch (_error) {
              toast.error(t('common:errors.unknown'));
            }
          }}
        >
          <Icon icon="download" className="me-sm size-5" />
          {t('api:download_openapi_spec_v1')}
        </Button>
      </div>
      {createdApiKey ? <CreatedAPIKey createdApiKey={createdApiKey} /> : null}
      <CollapsiblePaper.Container>
        <CollapsiblePaper.Title>
          <span className="flex-1">{t('settings:api_keys')}</span>
          {isCreateApiKeyAvailable ? <CreateApiKey /> : null}
        </CollapsiblePaper.Title>
        <CollapsiblePaper.Content>
          <Table.Container {...apiKeyTable.getContainerProps()} className="max-h-96">
            <Table.Header headerGroups={apiKeyTable.table.getHeaderGroups()} />
            <Table.Body {...apiKeyTable.getBodyProps()}>
              {apiKeyTable.rows.map((row) => {
                return <Table.Row key={row.id} className="hover:bg-purple-background-light group" row={row} />;
              })}
            </Table.Body>
          </Table.Container>
        </CollapsiblePaper.Content>
      </CollapsiblePaper.Container>
      {canReadWebhooks ? (
        <WebhooksSection
          webhooks={webhooks}
          webhooksError={webhooksError}
          isCreateWebhookAvailable={isCreateWebhookAvailable}
          webhooksStatus={webhooksStatus}
        />
      ) : null}
    </Page.Content>
  );
}

function CreatedAPIKey({ createdApiKey }: { createdApiKey: CreatedApiKey }) {
  const { t } = useTranslation(['settings']);
  return (
    <Callout variant="outlined">
      <div className="flex flex-col gap-xs">
        <span className="font-bold">{t('settings:api_keys.new_api_key')}</span>
        <span>{t('settings:api_keys.copy_api_key')}</span>
        <CopyToClipboardButton toCopy={createdApiKey.key}>
          <span className="text-s line-clamp-1 font-semibold">{createdApiKey.key}</span>
        </CopyToClipboardButton>
      </div>
    </Callout>
  );
}

function WebhooksSection({
  webhooks,
  webhooksError,
  isCreateWebhookAvailable,
  webhooksStatus,
}: {
  webhooks: Webhook[];
  webhooksError: boolean;
  isCreateWebhookAvailable: boolean;
  webhooksStatus: FeatureAccessLevelDto;
}) {
  const { t } = useTranslation(['settings']);

  const columns = useMemo(() => {
    return [
      webhookColumnHelper.accessor((row) => row.url, {
        id: 'url',
        header: t('settings:webhooks.url'),
        size: 200,
      }),
      webhookColumnHelper.accessor((row) => row.eventTypes, {
        id: 'eventTypes',
        header: t('settings:webhooks.event_types'),
        size: 200,
        cell: ({ getValue }) => {
          const eventTypes = getValue();
          if (eventTypes.length === 0) {
            return <span className="text-grey-disabled text-s">{t('settings:webhooks.event_types.placeholder')}</span>;
          }
          return <EventTypes eventTypes={eventTypes} />;
        },
      }),
    ];
  }, [t]);

  const { table, getBodyProps, rows, getContainerProps } = useTable({
    data: webhooks,
    columns,
    columnResizeMode: 'onChange',

    enableSorting: false,
    rowLink: (webhook) => <Link to="/settings/webhooks/$webhookId" params={{ webhookId: webhook.id }} />,
  });

  const webhooksTitleContent = (
    <span className="flex flex-1 items-center gap-sm">
      {t('settings:webhooks')}
      {webhooksStatus !== 'allowed' && !isAccessible(webhooksStatus) ? (
        <Nudge content="" kind={webhooksStatus} className="size-5" />
      ) : null}
    </span>
  );

  if (webhooksError || !isAccessible(webhooksStatus)) {
    return (
      <CollapsiblePaper.Container defaultOpen={false}>
        <CollapsiblePaper.Title>
          {webhooksTitleContent}
          <Button variant="primary" disabled>
            <Icon icon="plus" className="size-5" />
            {t('settings:webhooks.new_webhook')}
          </Button>
        </CollapsiblePaper.Title>
        <CollapsiblePaper.Content>
          <div className="flex flex-col items-center gap-md py-xl">
            <Typo variant="title2" className="text-grey-primary">
              {t('settings:webhooks.configuration_error')}
            </Typo>
            <Callout variant="outlined">
              <p className="whitespace-pre-wrap">
                <Trans
                  t={t}
                  i18nKey="settings:webhooks.convoy_error"
                  components={{
                    DocLink: <ExternalLink href={webhooksSetupDocHref} />,
                  }}
                />
              </p>
            </Callout>
          </div>
        </CollapsiblePaper.Content>
      </CollapsiblePaper.Container>
    );
  }

  return (
    <CollapsiblePaper.Container>
      <CollapsiblePaper.Title>
        {webhooksTitleContent}
        {isCreateWebhookAvailable ? (
          <span onClick={(e) => e.stopPropagation()}>
            <CreateWebhook webhookStatus={webhooksStatus}>
              <Button variant="primary">
                <Icon icon="plus" className="size-5" />
                {t('settings:webhooks.new_webhook')}
              </Button>
            </CreateWebhook>
          </span>
        ) : null}
      </CollapsiblePaper.Title>
      <CollapsiblePaper.Content>
        <Callout className="mb-md lg:mb-lg" variant="outlined">
          <p className="whitespace-pre-wrap">
            <Trans
              t={t}
              i18nKey="settings:webhooks.setup_documentation"
              components={{
                DocLink: <ExternalLink href={webhooksSetupDocHref} />,
              }}
            />
          </p>
        </Callout>
        <Table.Container {...getContainerProps()} className="max-h-96">
          <Table.Header headerGroups={table.getHeaderGroups()} />
          <Table.Body {...getBodyProps()}>
            {rows.map((row) => {
              return <Table.Row key={row.id} row={row} />;
            })}
          </Table.Body>
        </Table.Container>
      </CollapsiblePaper.Content>
    </CollapsiblePaper.Container>
  );
}
